import { Injectable } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  LawPersonAddressControlName,
  LawPersonControlName,
  LawPersonFormService,
  LawPersonType,
  LegalPersonControlName,
  ValidatePostalCode,
  emailValidator,
  getDefaultControlValue,
  nipValidator,
  phoneValidator,
  regonValidator,
  streetFromDictionaryValidator,
} from '@gk/gk-modules';
import { InvestorDetailsControlName } from './investor-details-form.model';

@Injectable()
export class InvestorDetailsFormService {
  constructor(
    private formBuilder: UntypedFormBuilder,
    private lawPersonFormService: LawPersonFormService,
  ) {}

  getFormGroup(
    isPhoneNumberRequired = true,
    isPhoneNumberValid = true,
    primitivePostalCode?: boolean,
    isEmailRequired = true,
    lawPersonType = LawPersonType.Natural,
    addressFormGroupBasedOnSelectControls = false,
    disableAddressFormInNaturalPersonGroup = false,
    proxyTypeControl = false,
  ): UntypedFormGroup {
    return this.formBuilder.group({
      [LawPersonControlName.Type]: [lawPersonType],
      ...(proxyTypeControl
        ? {
            [InvestorDetailsControlName.ProxyType]: [
              getDefaultControlValue(),
              [Validators.required],
            ],
          }
        : {}),
      [LawPersonControlName.LegalPersonData]: this.formBuilder.group({
        [LegalPersonControlName.Name]: [
          getDefaultControlValue(),
          [Validators.required],
        ],
        [LegalPersonControlName.Nip]: [
          getDefaultControlValue(),
          [Validators.required, nipValidator()],
        ],
        [LegalPersonControlName.Regon]: [
          getDefaultControlValue(),
          [Validators.required, regonValidator()],
        ],
        [LegalPersonControlName.TypeId]: [
          getDefaultControlValue(true),
          Validators.required,
        ],
      }),
      [LawPersonControlName.NaturalPersonData]:
        this.lawPersonFormService.getNaturalPersonFormGroup(),
      [InvestorDetailsControlName.IdNumber]: [getDefaultControlValue()],
      [InvestorDetailsControlName.PhoneNumber]: [
        getDefaultControlValue(),
        [
          isPhoneNumberRequired ? Validators.required : null,
          isPhoneNumberValid ? phoneValidator : null,
        ].filter(Boolean),
      ],
      [InvestorDetailsControlName.Email]: [
        getDefaultControlValue(),
        [isEmailRequired ? Validators.required : null, emailValidator].filter(
          Boolean,
        ),
      ],
      [InvestorDetailsControlName.EPUAPAddress]: [getDefaultControlValue()],
      ...(addressFormGroupBasedOnSelectControls
        ? {
            [LawPersonControlName.AddressSelect]: this.formBuilder.group({}),
            ...(disableAddressFormInNaturalPersonGroup
              ? {
                  [LawPersonControlName.Address]: this.getAddressFormGroup(
                    primitivePostalCode,
                    true,
                  ),
                }
              : {}),
          }
        : {
            [LawPersonControlName.Address]:
              this.getAddressFormGroup(primitivePostalCode),
          }),
    });
  }

  getAddressFormGroup(
    primitivePostalCode?: boolean,
    disabled?: boolean,
  ): UntypedFormGroup {
    return this.formBuilder.group(
      {
        [LawPersonAddressControlName.Place]: [
          getDefaultControlValue(disabled),
          Validators.required,
        ],
        [LawPersonAddressControlName.Street]: [
          getDefaultControlValue(disabled),
          Validators.required,
        ],
        [LawPersonAddressControlName.StreetAbsence]: false,
        [LawPersonAddressControlName.BuildingNumber]: [
          getDefaultControlValue(disabled),
          Validators.required,
        ],
        [LawPersonAddressControlName.LocalNumber]: [
          getDefaultControlValue(disabled),
        ],
        [LawPersonAddressControlName.PostalCode]: [
          getDefaultControlValue(disabled),
          primitivePostalCode
            ? [Validators.required, ValidatePostalCode]
            : [Validators.required],
        ],
        [LawPersonAddressControlName.PostOffice]: [
          getDefaultControlValue(disabled),
        ],
      },
      { validator: streetFromDictionaryValidator() },
    );
  }
}
